export type SeoFaq = {
  question: string;
  answer: string;
};

export type SeoContentBlock = {
  eyebrow: string;
  heading: string;
  intro: string;
  problem: string;
  builds: string[];
  industries: string[];
  stack: string[];
  process: string[];
  reasons: string[];
  relatedLinks: { label: string; href: string }[];
  faqs: SeoFaq[];
};

const commonProcess = [
  "Discovery and workflow mapping",
  "UX, database and integration planning",
  "Agile development with review checkpoints",
  "Testing, deployment and data migration",
  "Maintenance, support and continuous improvement",
];

const commonReasons = [
  "10+ years of software delivery experience",
  "Custom CRM, ERP, automation and communication products under one roof",
  "India-based team for long-term development and support",
  "Practical architecture focused on business workflows and measurable outcomes",
];

const baseLinks = [
  { label: "custom software development", href: "/services/software-development" },
  { label: "CRM software", href: "/crm" },
  { label: "portfolio examples", href: "/portfolio" },
  { label: "contact Variable Soft", href: "/getintouch" },
];

const aiStack = ["OpenAI APIs", "RAG", "Vector search", "Node.js", "Python", "React", "PostgreSQL", "CRM APIs", "WhatsApp API"];
const appStack = ["React", "TypeScript", "Node.js", "PHP", ".NET", "Python", "PostgreSQL", "MongoDB", "REST APIs", "Cloud hosting"];

function block(input: Partial<SeoContentBlock> & Pick<SeoContentBlock, "eyebrow" | "heading" | "intro" | "problem" | "builds" | "industries" | "stack" | "faqs">): SeoContentBlock {
  return {
    process: commonProcess,
    reasons: commonReasons,
    relatedLinks: baseLinks,
    ...input,
  };
}

export const seoContentByPath: Record<string, SeoContentBlock> = {
  "/service": block({
    eyebrow: "Software services",
    heading: "Software development services for CRM, ERP, AI automation and business workflows",
    intro:
      "Variable Soft provides software development services for companies that need business systems built around real operations. Our team works across custom CRM, ERP, web apps, mobile apps, AI automation, IVR, dialer, WhatsApp automation and support workflows.",
    problem:
      "Many businesses outgrow spreadsheets, disconnected SaaS tools and manual follow-up processes. A service-led software partner helps connect data, teams, approvals, reports and customer communication in one maintainable system.",
    builds: ["Custom CRM and ERP systems", "Web and mobile applications", "AI automation and dashboards", "IVR, dialer and cloud call center workflows", "Maintenance and support programs"],
    industries: ["Real estate", "Education", "Ecommerce", "Service businesses", "Call centers", "Operations teams"],
    stack: appStack,
    faqs: [
      { question: "What services does Variable Soft provide?", answer: "Variable Soft provides custom software development, CRM and ERP development, web and mobile apps, AI automation, IVR, dialer, cloud call center, WhatsApp API and support services." },
      { question: "Can services be combined into one project?", answer: "Yes. Many projects combine CRM, web app development, AI automation, communication tools, reporting dashboards and integrations." },
    ],
  }),
  "/webdesign": block({
    eyebrow: "Web design",
    heading: "SEO-ready web design focused on speed, clarity and conversion",
    intro:
      "A business website should explain what the company does, load quickly, work on mobile and guide visitors toward enquiry. Variable Soft designs responsive websites with clear content structure, technical SEO basics and conversion-focused layouts.",
    problem:
      "Outdated websites often lose leads because pages are slow, unclear, hard to update or weak for search engines. A structured web design project fixes navigation, page hierarchy, mobile usability, forms and content presentation.",
    builds: ["Corporate websites", "Landing pages", "Service pages", "Portfolio pages", "Lead generation forms", "SEO-ready page layouts"],
    industries: ["B2B services", "Startups", "Education", "Real estate", "Healthcare", "Local businesses"],
    stack: ["React", "HTML", "CSS", "Tailwind", "WordPress", "Analytics", "Technical SEO", "Performance optimization"],
    relatedLinks: [
      { label: "web application development", href: "/software-development-in-jaipur" },
      { label: "SEO services", href: "/it-companies-in-jaipur" },
      { label: "portfolio examples", href: "/portfolio" },
      { label: "request a website quote", href: "/getintouch" },
    ],
    faqs: [
      { question: "What is SEO-ready web design?", answer: "SEO-ready web design means building pages with clear headings, crawlable links, responsive layouts, metadata, structured content and performance foundations from the start." },
      { question: "Can Variable Soft redesign an existing website?", answer: "Yes. We can redesign existing websites while preserving important URLs, improving page structure and keeping contact forms and business content intact." },
    ],
  }),
  "/software-development-in-jaipur": block({
    eyebrow: "Web application development",
    heading: "Web application development company in Jaipur for scalable business systems",
    intro:
      "Variable Soft builds web applications for businesses in Jaipur, India and global markets. These systems include portals, dashboards, CRM modules, ERP workflows, APIs, reporting tools and automation layers.",
    problem:
      "Generic websites cannot manage complex business workflows. When teams need user roles, approvals, data entry, reporting, integrations and secure access, a custom web application is usually the right fit.",
    builds: ["Admin portals", "Customer dashboards", "API-connected web apps", "CRM and ERP modules", "Reporting and analytics panels", "Workflow automation tools"],
    industries: ["Manufacturing", "Real estate", "Education", "Finance operations", "Ecommerce", "Professional services"],
    stack: appStack,
    faqs: [
      { question: "What is web application development?", answer: "Web application development is building browser-based software that supports workflows, dashboards, user roles, forms, databases, APIs and business reporting." },
      { question: "Does Variable Soft serve clients outside Jaipur?", answer: "Yes. Variable Soft is based in Jaipur and serves companies across India and worldwide." },
    ],
  }),
  "/ecommerce-development-services": block({
    eyebrow: "Ecommerce development",
    heading: "Ecommerce development for catalogs, ordering, payments and operations",
    intro:
      "Variable Soft builds ecommerce websites and custom commerce systems for companies that need product catalogs, cart flows, payment integration, order management, inventory visibility and customer communication.",
    problem:
      "Commerce teams often need more than a storefront. They need stock control, order assignment, invoicing, CRM follow-ups, shipping updates and reports that match how the business operates.",
    builds: ["Online stores", "Product catalogs", "Payment and checkout flows", "Order dashboards", "Inventory integrations", "Customer communication workflows"],
    industries: ["Retail", "D2C brands", "Wholesale", "Manufacturing", "Local stores", "Service commerce"],
    stack: ["React", "Node.js", "PHP", "WooCommerce", "Payment gateways", "PostgreSQL", "APIs", "Analytics"],
    relatedLinks: [
      { label: "custom software development", href: "/services/software-development" },
      { label: "web design services", href: "/webdesign" },
      { label: "CRM software", href: "/crm" },
      { label: "start an ecommerce project", href: "/getintouch" },
    ],
    faqs: [
      { question: "Can ecommerce be integrated with CRM?", answer: "Yes. Ecommerce orders, enquiries and customer records can be connected to CRM workflows for follow-up, support and repeat sales." },
      { question: "Do you build custom ecommerce platforms?", answer: "Yes. Variable Soft can build custom ecommerce platforms when standard templates do not fit product, pricing, approval or operations needs." },
    ],
  }),
  "/top-software-companies-in-jaipur": block({
    eyebrow: "Mobile app development",
    heading: "Mobile app development for Android, iOS and connected business workflows",
    intro:
      "Variable Soft develops mobile apps that connect customers, field teams, sales teams and operations with backend systems. We build apps for lead capture, ecommerce, task management, notifications, reporting and service workflows.",
    problem:
      "A mobile app should not become an isolated tool. It needs reliable APIs, secure authentication, clean UI, notifications, analytics and a backend that can scale with the business.",
    builds: ["Android apps", "iOS apps", "Cross-platform apps", "API backends", "Push notification flows", "Mobile dashboards"],
    industries: ["Field sales", "Ecommerce", "Education", "Real estate", "Service teams", "Internal operations"],
    stack: ["React Native", "Flutter", "Android", "iOS", "Node.js", "REST APIs", "Firebase", "PostgreSQL"],
    faqs: [
      { question: "Can mobile apps connect with CRM or ERP?", answer: "Yes. Mobile apps can sync with CRM, ERP, dashboards, payment systems, WhatsApp workflows and other business APIs." },
      { question: "Which app platforms do you support?", answer: "Variable Soft can build Android, iOS and cross-platform mobile apps depending on project goals, budget and maintenance needs." },
    ],
  }),
  "/it-companies-in-jaipur": block({
    eyebrow: "SEO and digital growth",
    heading: "Technical SEO and digital growth for software and business websites",
    intro:
      "Variable Soft supports SEO through better site structure, technical fixes, content planning, landing pages, analytics and development changes that help users and search engines understand a business.",
    problem:
      "SEO fails when pages have weak metadata, poor content hierarchy, slow performance, broken internal links or unclear service positioning. Technical SEO connects content, development and conversion work.",
    builds: ["Technical SEO audits", "SEO-ready landing pages", "Metadata and sitemap improvements", "Structured data implementation", "Analytics setup", "Conversion-focused forms"],
    industries: ["Software companies", "Local businesses", "B2B services", "Ecommerce", "Education", "Real estate"],
    stack: ["Schema.org", "Google Search Console", "Analytics", "React SEO", "Sitemaps", "Robots.txt", "Core Web Vitals"],
    faqs: [
      { question: "What is technical SEO?", answer: "Technical SEO improves crawlability, indexing, page structure, metadata, internal links, schema, performance and mobile usability." },
      { question: "Can SEO be handled during development?", answer: "Yes. The best SEO foundations are built during development through clean URLs, metadata, headings, schema, speed and content structure." },
    ],
  }),
  "/services/micro-gcc-india": block({
    eyebrow: "Micro-GCC India",
    heading: "Micro-GCC and dedicated development teams in India",
    intro:
      "Variable Soft helps companies set up focused offshore software teams in India for product engineering, CRM development, automation, QA, maintenance and long-term support.",
    problem:
      "Hiring a full internal team can be slow and expensive. A Micro-GCC model gives companies dedicated capability with practical governance, communication and delivery control.",
    builds: ["Dedicated developers", "QA and support teams", "CRM and automation squads", "Maintenance retainers", "Product engineering support", "Reporting and delivery governance"],
    industries: ["SaaS", "Agencies", "Enterprises", "Startups", "Operations teams", "International businesses"],
    stack: appStack,
    faqs: [
      { question: "What is a Micro-GCC?", answer: "A Micro-GCC is a focused offshore capability team that supports product development, maintenance, automation or operations without setting up a large captive center." },
      { question: "Can the team work on existing software?", answer: "Yes. Dedicated teams can maintain, improve and extend existing applications after onboarding and codebase review." },
    ],
  }),
  "/services/whatsapp-business-api": block({
    eyebrow: "WhatsApp Business API",
    heading: "WhatsApp Business API integration for sales, support and CRM workflows",
    intro:
      "Variable Soft implements WhatsApp Business API workflows for lead responses, support automation, alerts, campaigns, CRM updates and chatbot handoff.",
    problem:
      "WhatsApp conversations become hard to manage when leads, support messages and follow-ups are handled manually across phones. API-based workflows help teams respond faster and track communication.",
    builds: ["API setup", "CRM integration", "Template message workflows", "Chatbot flows", "Human handoff", "Conversation reporting"],
    industries: ["Sales teams", "Real estate", "Education", "Healthcare", "Ecommerce", "Support teams"],
    stack: ["WhatsApp Business API", "Node.js", "CRM APIs", "Webhooks", "Chatbots", "PostgreSQL", "Analytics"],
    faqs: [
      { question: "Can WhatsApp be connected to CRM?", answer: "Yes. WhatsApp enquiries, replies and follow-up statuses can be connected with CRM records and sales workflows." },
      { question: "Does WhatsApp API support automation?", answer: "Yes. It can support template messages, chatbot replies, lead routing, reminders, alerts and human handoff." },
    ],
  }),
  "/maintenanceandsupport": block({
    eyebrow: "Maintenance and support",
    heading: "Software maintenance and support for stable business applications",
    intro:
      "Variable Soft supports existing software through bug fixes, hosting help, feature improvements, security updates, integrations, performance checks and user support.",
    problem:
      "Business software needs ongoing care after launch. Without maintenance, small bugs, server issues, outdated dependencies and workflow changes can create downtime or data problems.",
    builds: ["Bug fixing", "Feature enhancements", "Hosting and deployment support", "Performance improvements", "Security updates", "Integration maintenance"],
    industries: ["SaaS", "CRM users", "ERP users", "Ecommerce", "Internal operations", "Service teams"],
    stack: appStack,
    faqs: [
      { question: "Can Variable Soft support software built by another team?", answer: "Yes. We can review the codebase, hosting and documentation before creating a practical support plan." },
      { question: "What is included in software maintenance?", answer: "Maintenance can include bug fixes, updates, monitoring, performance improvements, feature changes, hosting support and user help." },
    ],
  }),
  "/cloud-call-center": block({
    eyebrow: "Cloud call center",
    heading: "Cloud call center workflows connected with CRM, IVR and dialer systems",
    intro:
      "Variable Soft builds cloud call center systems for sales and support teams that need call routing, dialer automation, IVR, reporting, recordings and CRM-connected follow-ups.",
    problem:
      "Call center teams lose visibility when calls, leads, notes and follow-ups live in separate systems. Connecting calling workflows with CRM improves accountability and response time.",
    builds: ["Cloud calling workflows", "Dialer integration", "IVR routing", "Call logs and reports", "CRM sync", "WhatsApp follow-up integration"],
    industries: ["Call centers", "Real estate", "Education", "Financial services", "Support teams", "Sales teams"],
    stack: ["VoIP", "SIP", "IVR", "Dialer APIs", "CRM APIs", "Node.js", "Dashboards"],
    faqs: [
      { question: "Can cloud call center software connect with CRM?", answer: "Yes. Calls, notes, leads, recordings and follow-up statuses can sync with CRM workflows." },
      { question: "What teams use cloud call center systems?", answer: "Sales, support, admissions, real estate, finance and service teams use cloud call center systems to manage high call volumes." },
    ],
  }),
  "/ivr-system": block({
    eyebrow: "IVR system",
    heading: "IVR system development for call routing and customer communication",
    intro:
      "Variable Soft develops IVR systems that route calls, capture customer input, connect departments, support callbacks and integrate with CRM or call center workflows.",
    problem:
      "Manual call handling creates missed calls, slow routing and inconsistent customer experiences. IVR improves routing and lets teams handle inbound calls with clearer structure.",
    builds: ["Inbound IVR flows", "Department routing", "Callback workflows", "CRM-connected call logs", "Voicemail and alerts", "Call reports"],
    industries: ["Support centers", "Sales teams", "Healthcare", "Education", "Real estate", "Service companies"],
    stack: ["IVR", "VoIP", "SIP", "PBX", "CRM APIs", "Node.js", "Call analytics"],
    faqs: [
      { question: "What is an IVR system?", answer: "An IVR system is an automated phone menu that routes callers, captures inputs and connects calls to the right team or workflow." },
      { question: "Can IVR be integrated with CRM?", answer: "Yes. IVR calls can be logged against leads or customers in CRM for tracking, follow-up and reporting." },
    ],
  }),
  "/crm": block({
    eyebrow: "CRM software",
    heading: "CRM software and CRM development for lead management and sales automation",
    intro:
      "Variable Soft builds CRM systems for lead capture, follow-up tracking, sales assignment, dashboards, communication workflows and customer management.",
    problem:
      "Sales teams miss opportunities when leads are spread across calls, WhatsApp, portals, spreadsheets and email. CRM centralizes leads, tasks, reminders, history and reports.",
    builds: ["Lead management", "Contact management", "Task assignment", "Sales dashboards", "Justdial and Sulekha integrations", "WhatsApp and call workflows"],
    industries: ["Real estate", "Education", "Sales teams", "Service companies", "Call centers", "B2B businesses"],
    stack: ["React", "Node.js", "PostgreSQL", "CRM APIs", "WhatsApp API", "Dialer integration", "Analytics"],
    relatedLinks: [
      { label: "CRM features", href: "/crm/features" },
      { label: "what is CRM", href: "/crm/what-is-crm" },
      { label: "Justdial CRM integration", href: "/crm/justdial-integration" },
      { label: "contact Variable Soft", href: "/getintouch" },
    ],
    faqs: [
      { question: "What does CRM software do?", answer: "CRM software helps teams capture leads, manage contacts, assign tasks, track follow-ups, view communication history and report sales performance." },
      { question: "Can CRM be customized?", answer: "Yes. CRM can be customized around lead sources, sales stages, roles, reports, communication channels and integrations." },
    ],
  }),
  "/products/dialer": block({
    eyebrow: "Dialer software",
    heading: "Dialer software for sales calling, support teams and CRM-connected follow-ups",
    intro:
      "Variable Soft provides dialer software workflows for teams that need efficient outbound calling, call logging, reporting, lead assignment and CRM synchronization.",
    problem:
      "Manual dialing slows sales and support teams. Dialer systems improve productivity by organizing call queues, tracking outcomes and connecting call activity with customer records.",
    builds: ["Outbound dialer workflows", "Call disposition tracking", "CRM sync", "Call reports", "Lead queues", "Follow-up automation"],
    industries: ["Sales teams", "Call centers", "Education", "Real estate", "Finance", "Support teams"],
    stack: ["VoIP", "Dialer APIs", "CRM APIs", "React", "Node.js", "PostgreSQL", "Analytics"],
    faqs: [
      { question: "What is dialer software?", answer: "Dialer software helps teams place and track calls more efficiently, manage call queues, log outcomes and connect activity with CRM records." },
      { question: "Can dialer software integrate with CRM?", answer: "Yes. Dialer activity can sync with CRM leads, contacts, tasks and reports." },
    ],
  }),
  "/products/erp": block({
    eyebrow: "ERP software",
    heading: "ERP software for billing, inventory, approvals and business process management",
    intro:
      "Variable Soft builds ERP software for companies that need connected workflows across billing, purchase, inventory, fulfillment, approvals, reporting and operations.",
    problem:
      "Operations become difficult when stock, orders, invoices, approvals and reports are handled in separate files. ERP connects these processes into one controlled system.",
    builds: ["Billing modules", "Inventory management", "Purchase workflows", "Approval flows", "Role-based dashboards", "Operational reports"],
    industries: ["Manufacturing", "Trading", "Distribution", "Retail", "Service operations", "Back-office teams"],
    stack: appStack,
    faqs: [
      { question: "What is ERP software?", answer: "ERP software connects core business operations such as billing, inventory, purchases, approvals, reporting and process management." },
      { question: "Can ERP be developed module by module?", answer: "Yes. ERP can be built in phases, starting with the highest-impact workflow and adding modules over time." },
    ],
  }),
  "/products/taskmanagement": block({
    eyebrow: "Task management software",
    heading: "Task management software for assignments, accountability and workflow visibility",
    intro:
      "Variable Soft builds task management systems for teams that need assignment tracking, deadlines, priorities, progress visibility, reminders and reports.",
    problem:
      "Tasks get delayed when ownership, deadlines and status updates are scattered across chat and spreadsheets. A structured task system improves accountability and daily visibility.",
    builds: ["Task assignment", "Project dashboards", "Priority and deadline tracking", "Team reports", "Notifications", "Role-based views"],
    industries: ["Software teams", "Operations", "Sales support", "Service teams", "Back-office teams", "Management teams"],
    stack: appStack,
    faqs: [
      { question: "Who needs task management software?", answer: "Any team that needs assignment visibility, deadline tracking, status updates and accountability can use task management software." },
      { question: "Can task management connect with CRM?", answer: "Yes. CRM leads, support tickets or project milestones can automatically create tasks and reminders." },
    ],
  }),
  "/products/hosting": block({
    eyebrow: "Hosting services",
    heading: "Hosting support for business websites and web applications",
    intro:
      "Variable Soft supports hosting for websites, web applications and business systems that need reliable deployment, domain setup, server configuration, backups and technical help.",
    problem:
      "Business websites can suffer from downtime, slow loading, weak configuration or poor support. Proper hosting setup helps keep public pages, forms and applications available.",
    builds: ["Website hosting setup", "Application deployment", "Domain and DNS help", "Email and database support", "Backup planning", "Server troubleshooting"],
    industries: ["Business websites", "Web apps", "Ecommerce", "CRM users", "Agencies", "Internal tools"],
    stack: ["Linux hosting", "cPanel", "DNS", "SSL", "Databases", "Node.js", "PHP", "Monitoring"],
    faqs: [
      { question: "Does Variable Soft provide hosting support?", answer: "Yes. Variable Soft helps with hosting setup, deployment, DNS, SSL, database support and troubleshooting for business websites and applications." },
      { question: "Can hosting support include existing websites?", answer: "Yes. Existing websites can be reviewed and moved, configured or supported depending on their technology and hosting access." },
    ],
  }),
  "/products/sslcertificate": block({
    eyebrow: "SSL certificate services",
    heading: "SSL certificate setup for secure websites and applications",
    intro:
      "Variable Soft helps businesses configure SSL certificates so websites, forms, APIs and applications use secure HTTPS connections.",
    problem:
      "Missing or misconfigured SSL creates browser warnings, trust issues and security risk. Correct SSL setup protects user confidence and supports SEO-safe indexing.",
    builds: ["SSL installation", "HTTPS configuration", "Certificate renewal support", "Mixed-content checks", "Redirect checks", "Hosting coordination"],
    industries: ["Business websites", "Ecommerce", "CRM systems", "Web apps", "Landing pages", "Client portals"],
    stack: ["SSL/TLS", "HTTPS", "cPanel", "Nginx", "Apache", "DNS", "Hosting panels"],
    faqs: [
      { question: "Why does a website need SSL?", answer: "SSL enables HTTPS, protects data in transit and prevents browser security warnings that can reduce visitor trust." },
      { question: "Can SSL issues affect SEO?", answer: "Yes. Broken HTTPS, mixed content or certificate warnings can hurt user trust and crawl quality." },
    ],
  }),
  "/products/ivr": block({
    eyebrow: "IVR software",
    heading: "IVR software for automated phone menus, routing and call workflows",
    intro:
      "Variable Soft provides IVR software workflows for businesses that need automated call routing, voicemail, callbacks, phone surveys and CRM-connected communication.",
    problem:
      "Inbound calls become hard to manage without structured routing. IVR helps callers reach the right department and gives teams better visibility into call activity.",
    builds: ["Automated menus", "Call routing", "Voicemail alerts", "Callback flows", "Phone surveys", "CRM call logs"],
    industries: ["Support teams", "Sales teams", "Education", "Healthcare", "Real estate", "Call centers"],
    stack: ["IVR", "VoIP", "SIP", "PBX", "CRM APIs", "Call analytics"],
    faqs: [
      { question: "What does IVR software do?", answer: "IVR software answers calls, presents menu options, routes callers and can capture inputs or connect calls with CRM workflows." },
      { question: "Can IVR handle high call volumes?", answer: "Yes. IVR workflows can be designed for scalable call routing depending on phone system and channel capacity." },
    ],
  }),
  "/products/digisociety": block({
    eyebrow: "Digital society software",
    heading: "Digital society management software for member records, billing and communication",
    intro:
      "Variable Soft builds digital society management software for residential communities that need member records, communication, billing, requests and operational visibility.",
    problem:
      "Society operations become difficult when billing, notices, complaints and member records are handled manually. Digital workflows improve transparency and reduce admin effort.",
    builds: ["Member records", "Billing workflows", "Notice communication", "Complaint tracking", "Amenity requests", "Reports"],
    industries: ["Residential societies", "Apartment communities", "Facility teams", "Society admins", "Resident support", "Maintenance teams"],
    stack: ["React", "Node.js", "Mobile apps", "Notifications", "Databases", "Payment integrations", "Dashboards"],
    faqs: [
      { question: "What is digital society management software?", answer: "It is software that helps societies manage members, billing, notices, complaints, amenities and communication from one system." },
      { question: "Can society software include mobile access?", answer: "Yes. Resident and admin workflows can be made available through web or mobile app interfaces." },
    ],
  }),
  "/crm/what-is-crm": block({
    eyebrow: "CRM guide",
    heading: "What is CRM and how it helps sales, support and customer management",
    intro:
      "CRM stands for customer relationship management. It helps businesses organize leads, contacts, tasks, communication history, follow-ups and reports in one system.",
    problem:
      "Without CRM, teams often lose track of enquiries, duplicate follow-ups, miss sales opportunities and lack visibility into customer communication.",
    builds: ["Lead capture", "Contact management", "Sales stages", "Task reminders", "Communication history", "Reports"],
    industries: ["Sales teams", "Service businesses", "Real estate", "Education", "Support teams", "B2B companies"],
    stack: ["CRM workflows", "Dashboards", "WhatsApp API", "Dialer integration", "Email workflows", "Reports"],
    relatedLinks: [
      { label: "CRM software", href: "/crm" },
      { label: "CRM features", href: "/crm/features" },
      { label: "custom CRM development", href: "/services/software-development" },
      { label: "contact Variable Soft", href: "/getintouch" },
    ],
    faqs: [
      { question: "What is CRM?", answer: "CRM is software for managing leads, customers, follow-ups, tasks, communication and sales reporting." },
      { question: "Why do businesses use CRM?", answer: "Businesses use CRM to reduce missed follow-ups, improve visibility, organize contacts and measure sales or support performance." },
    ],
  }),
  "/crm/crm-software": block({
    eyebrow: "CRM software guide",
    heading: "CRM software for lead tracking, follow-ups and business growth",
    intro:
      "CRM software helps teams capture enquiries, assign leads, schedule follow-ups, track communication, manage tasks and understand sales performance.",
    problem:
      "Teams that rely on spreadsheets and chat history often lack clear ownership, reminders and reporting. CRM software brings structure to sales and service workflows.",
    builds: ["Lead pipelines", "Customer records", "Task tracking", "Follow-up reminders", "Sales reports", "Communication logs"],
    industries: ["Sales", "Real estate", "Education", "Services", "Call centers", "B2B businesses"],
    stack: ["CRM", "React", "Node.js", "PostgreSQL", "WhatsApp API", "Dialer APIs", "Dashboards"],
    relatedLinks: [
      { label: "CRM features", href: "/crm/features" },
      { label: "Justdial integration", href: "/crm/justdial-integration" },
      { label: "Sulekha integration", href: "/crm/sulekha" },
      { label: "contact Variable Soft", href: "/getintouch" },
    ],
    faqs: [
      { question: "What is CRM software used for?", answer: "CRM software is used to manage leads, customers, follow-ups, communication records, tasks and reports." },
      { question: "Can CRM software be customized by industry?", answer: "Yes. CRM workflows can be customized for real estate, education, services, call centers, ecommerce and B2B sales." },
    ],
  }),
  "/crm/features": block({
    eyebrow: "CRM features",
    heading: "CRM software features for contact management, lead tracking and reporting",
    intro:
      "CRM features help teams manage contacts, capture leads, schedule tasks, view dashboards, track communication and improve customer relationships.",
    problem:
      "A CRM is only useful when features match the team's workflow. Lead sources, roles, reports, communication tools and follow-up rules should fit the business process.",
    builds: ["Contact management", "Lead tracking", "Task assignment", "Dashboards", "Reports", "Communication integrations"],
    industries: ["Sales teams", "Support", "Real estate", "Education", "B2B services", "Call centers"],
    stack: ["CRM modules", "Dashboards", "Reports", "WhatsApp API", "Dialer APIs", "Email workflows"],
    relatedLinks: [
      { label: "CRM software", href: "/crm" },
      { label: "what is CRM", href: "/crm/what-is-crm" },
      { label: "AI CRM lead scoring", href: "/services/ai-crm-lead-scoring" },
      { label: "contact Variable Soft", href: "/getintouch" },
    ],
    faqs: [
      { question: "Which CRM features matter most?", answer: "Important CRM features include lead management, contact history, task reminders, dashboards, reports and communication integration." },
      { question: "Can CRM features be added later?", answer: "Yes. CRM can be improved module by module as business needs become clearer." },
    ],
  }),
  "/crm/justdial-integration": block({
    eyebrow: "Justdial CRM integration",
    heading: "Justdial CRM integration for faster lead capture and follow-up",
    intro:
      "Variable Soft connects Justdial enquiries with CRM workflows so teams can capture leads, assign owners, send responses, track follow-ups and measure conversion.",
    problem:
      "Manual lead copying from Justdial can delay response and create missed opportunities. Integration brings enquiries into CRM faster with structured tracking.",
    builds: ["Lead capture", "Auto assignment", "Follow-up reminders", "Response workflows", "Conversion reports", "CRM dashboards"],
    industries: ["Local services", "Real estate", "Education", "Healthcare", "B2B services", "Sales teams"],
    stack: ["CRM APIs", "Lead APIs", "Webhooks", "React", "Node.js", "Reports"],
    relatedLinks: [
      { label: "CRM software", href: "/crm" },
      { label: "CRM features", href: "/crm/features" },
      { label: "Sulekha CRM integration", href: "/crm/sulekha" },
      { label: "contact Variable Soft", href: "/getintouch" },
    ],
    faqs: [
      { question: "What is Justdial CRM integration?", answer: "It connects Justdial enquiries with CRM so leads can be assigned, followed up and reported without manual copying." },
      { question: "Can responses be automated?", answer: "Yes. CRM workflows can trigger response messages, tasks and follow-up reminders." },
    ],
  }),
  "/crm/sulekha": block({
    eyebrow: "Sulekha CRM integration",
    heading: "Sulekha CRM integration for enquiry tracking and sales automation",
    intro:
      "Variable Soft connects Sulekha enquiries to CRM workflows for lead capture, assignment, follow-up tracking, instant responses and reporting.",
    problem:
      "Sulekha enquiries can be missed when handled manually. CRM integration helps sales teams respond quickly and measure every lead source.",
    builds: ["Sulekha enquiry capture", "Lead assignment", "Follow-up reminders", "Instant response workflows", "Lead reports", "CRM dashboards"],
    industries: ["Education", "Local services", "Real estate", "Healthcare", "B2B services", "Sales teams"],
    stack: ["CRM APIs", "Lead APIs", "Webhooks", "Node.js", "Dashboards", "Reports"],
    relatedLinks: [
      { label: "CRM software", href: "/crm" },
      { label: "Justdial CRM integration", href: "/crm/justdial-integration" },
      { label: "CRM features", href: "/crm/features" },
      { label: "contact Variable Soft", href: "/getintouch" },
    ],
    faqs: [
      { question: "What is Sulekha CRM integration?", answer: "It brings Sulekha enquiries into CRM for assignment, follow-up, response tracking and reporting." },
      { question: "Can Sulekha leads be reported separately?", answer: "Yes. CRM dashboards can show source-wise leads, follow-ups and conversion status." },
    ],
  }),
};

const aiServiceContent: Record<string, Pick<SeoContentBlock, "eyebrow" | "heading" | "intro" | "problem" | "builds" | "industries" | "faqs">> = {
  "/services/ai-chatbot-development": {
    eyebrow: "AI chatbot development",
    heading: "AI chatbot development for websites, WhatsApp, CRM and support workflows",
    intro: "Variable Soft builds AI chatbots that answer customer questions, qualify leads, support internal teams and connect conversations with CRM or business systems.",
    problem: "Support and sales teams spend time answering repeat questions. AI chatbots reduce response time while preserving human handoff for complex cases.",
    builds: ["Website chatbots", "WhatsApp AI bots", "Knowledge-base Q&A", "CRM lead capture", "Human handoff", "Conversation analytics"],
    industries: ["Sales", "Support", "Education", "Ecommerce", "Real estate", "Internal teams"],
    faqs: [
      { question: "What is AI chatbot development?", answer: "AI chatbot development means building conversational assistants that answer questions, collect data, qualify users and connect with business workflows." },
      { question: "Can AI chatbots use company documents?", answer: "Yes. A chatbot can use approved knowledge bases, FAQs, documents or CRM data through retrieval-based workflows." },
    ],
  },
  "/services/whatsapp-ai-response": {
    eyebrow: "WhatsApp AI response",
    heading: "WhatsApp AI response automation for faster lead and support replies",
    intro: "Variable Soft builds WhatsApp AI response workflows that answer common questions, qualify leads, collect details and pass conversations to human teams when needed.",
    problem: "Manual WhatsApp replies can be slow during high enquiry volume. AI response automation keeps conversations moving and records important data.",
    builds: ["AI replies", "Lead qualification", "FAQ automation", "CRM sync", "Human handoff", "Conversation summaries"],
    industries: ["Real estate", "Education", "Support", "Ecommerce", "Sales", "Healthcare"],
    faqs: [
      { question: "Can WhatsApp AI replies be reviewed by humans?", answer: "Yes. Workflows can include human handoff, approval and escalation rules." },
      { question: "Can WhatsApp AI connect to CRM?", answer: "Yes. Conversations can create or update CRM leads, tasks and follow-up records." },
    ],
  },
  "/services/ai-agent-automation": {
    eyebrow: "AI agent automation",
    heading: "AI agent automation for follow-ups, reports, triage and workflow tasks",
    intro: "Variable Soft builds AI agents that assist with repetitive business tasks such as follow-ups, ticket triage, report summaries, data checks and CRM updates.",
    problem: "Teams often lose hours on repeat tasks that follow clear rules. AI agents can reduce manual effort when connected safely with existing software and approvals.",
    builds: ["Follow-up agents", "Ticket triage", "Report summaries", "CRM task automation", "Approval helpers", "Data quality checks"],
    industries: ["Sales", "Support", "Operations", "Finance", "HR", "Management"],
    faqs: [
      { question: "What is an AI agent?", answer: "An AI agent is a workflow assistant that can interpret a task, use approved tools or data, and help complete repeat business actions." },
      { question: "Are AI agents connected to existing software?", answer: "Yes. AI agents can connect with CRM, ERP, dashboards, documents, email or APIs with suitable controls." },
    ],
  },
  "/services/generative-ai-integration": {
    eyebrow: "Generative AI integration",
    heading: "Generative AI integration for SaaS, CRM and business applications",
    intro: "Variable Soft integrates generative AI into existing software for summaries, content drafts, semantic search, smart forms, dashboards and workflow assistance.",
    problem: "Many products can benefit from AI features, but integration must be controlled, useful and connected to real user workflows rather than added as a novelty.",
    builds: ["AI summaries", "Smart forms", "Semantic search", "Drafting assistants", "Knowledge retrieval", "Dashboard insights"],
    industries: ["SaaS", "CRM", "Education", "Support", "Operations", "Content teams"],
    faqs: [
      { question: "What is generative AI integration?", answer: "Generative AI integration adds AI-powered text, search, summary, assistant or automation features to existing software." },
      { question: "Can AI features be added to an existing app?", answer: "Yes. Existing apps can add AI features through APIs, database access, retrieval systems and controlled user workflows." },
    ],
  },
  "/services/ai-crm-lead-scoring": {
    eyebrow: "AI CRM and lead scoring",
    heading: "AI CRM lead scoring for prioritizing sales follow-ups",
    intro: "Variable Soft upgrades CRM systems with AI lead scoring, follow-up suggestions, conversation summaries and sales workflow automation.",
    problem: "Sales teams often treat all leads equally. Lead scoring helps teams prioritize prospects based on source, engagement, conversation quality and follow-up history.",
    builds: ["Lead scoring", "Follow-up suggestions", "CRM summaries", "Sales dashboards", "WhatsApp insights", "Task automation"],
    industries: ["Sales teams", "Real estate", "Education", "B2B services", "Call centers", "Financial services"],
    faqs: [
      { question: "What is AI lead scoring?", answer: "AI lead scoring ranks leads by likely value or urgency using available CRM, conversation, activity and source data." },
      { question: "Can lead scoring work with existing CRM data?", answer: "Yes. Lead scoring can use existing CRM records if the data is clean enough and fields are meaningful." },
    ],
  },
  "/services/ai-document-processing": {
    eyebrow: "AI document processing",
    heading: "AI document processing for invoices, resumes, forms and PDFs",
    intro: "Variable Soft builds AI document processing workflows that extract data from PDFs, forms, invoices, KYC documents, resumes and email attachments.",
    problem: "Manual document entry is slow and error-prone. AI extraction can move structured information into CRM, ERP, dashboards or approval workflows faster.",
    builds: ["Invoice extraction", "Resume parsing", "KYC checks", "PDF data extraction", "Form processing", "ERP or CRM sync"],
    industries: ["Finance", "HR", "Operations", "Education", "Healthcare", "Back-office teams"],
    faqs: [
      { question: "What is AI document processing?", answer: "AI document processing extracts and structures information from documents so teams can review, approve and sync data faster." },
      { question: "Can extracted data go into ERP or CRM?", answer: "Yes. Extracted data can be validated and sent to ERP, CRM, dashboards or approval systems." },
    ],
  },
  "/services/ai-data-analytics": {
    eyebrow: "AI data analytics",
    heading: "AI data analytics and BI dashboards for practical business decisions",
    intro: "Variable Soft builds analytics dashboards, data pipelines and AI-assisted insight systems for CRM, ERP, website, sales and operations data.",
    problem: "Business data is often available but hard to interpret. Dashboards and AI-assisted analytics make trends, risks and actions easier to see.",
    builds: ["BI dashboards", "Forecasting", "Data pipelines", "CRM analytics", "ERP reporting", "AI-assisted insights"],
    industries: ["Sales", "Operations", "Finance", "Ecommerce", "Management", "Support"],
    faqs: [
      { question: "What is AI data analytics?", answer: "AI data analytics uses data processing, dashboards and AI-assisted interpretation to help teams understand trends and make decisions." },
      { question: "Can analytics connect multiple systems?", answer: "Yes. Analytics can combine CRM, ERP, website, marketing, sales and support data when integrations are available." },
    ],
  },
};

Object.entries(aiServiceContent).forEach(([path, content]) => {
  seoContentByPath[path] = block({
    ...content,
    stack: aiStack,
    relatedLinks: [
      { label: "AI automation services", href: "/services/ai-agent-automation" },
      { label: "AI CRM lead scoring", href: "/services/ai-crm-lead-scoring" },
      { label: "WhatsApp Business API", href: "/services/whatsapp-business-api" },
      { label: "discuss an AI project", href: "/getintouch" },
    ],
  });
});

export function getSeoContent(pathname: string) {
  return seoContentByPath[pathname];
}
